import type { FC } from 'react';
import { cn } from '@components/lib/utils';
import Tooltip from '@components/Tooltip';
import { Button } from '@components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@components/ui/dropdown-menu';
import { POPOVER_Z_INDEX } from '@constants/z-indices';
import { ChevronDown, ChevronsDown, ChevronsUp } from 'lucide-react';

export type FindingsFilter = 'all' | 'open' | 'reviewed';

export const FILTER_LABEL: Record<FindingsFilter, string> = {
  all: 'Show all suggestions',
  open: 'Open suggestions only',
  reviewed: 'Reviewed suggestions only',
};

const FILTERS: FindingsFilter[] = ['all', 'open', 'reviewed'];

interface FindingsToolbarProps {
  filter: FindingsFilter;
  onFilterChange: (filter: FindingsFilter) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
}

/** Filter by review state on the left, expand / collapse everything on the right. */
export const FindingsToolbar: FC<FindingsToolbarProps> = ({ filter, onFilterChange, onExpandAll, onCollapseAll }) => (
  <div className="flex items-center gap-2">
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="h-8 min-w-0 flex-1 justify-between px-3 text-[13px] font-normal text-gray-700"
          data-testid="pbqa-filter"
        >
          <span className="truncate">{FILTER_LABEL[filter]}</span>
          <ChevronDown className="text-gray-500" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      {/* The menu portals to body; without this it renders under the drawer (z-997). */}
      <DropdownMenuContent align="start" className={cn('w-60', POPOVER_Z_INDEX)}>
        <DropdownMenuRadioGroup value={filter} onValueChange={(value) => onFilterChange(value as FindingsFilter)}>
          {FILTERS.map((option) => (
            <DropdownMenuRadioItem key={option} value={option} data-testid={`pbqa-filter-${option}`}>
              {FILTER_LABEL[option]}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
    <Tooltip content="Expand all">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label="Expand all"
        data-testid="pbqa-expand-all"
        onClick={onExpandAll}
      >
        <ChevronsDown className="text-gray-600" aria-hidden />
      </Button>
    </Tooltip>
    <Tooltip content="Collapse all">
      <Button
        variant="outline"
        size="icon-sm"
        aria-label="Collapse all"
        data-testid="pbqa-collapse-all"
        onClick={onCollapseAll}
      >
        <ChevronsUp className="text-gray-600" aria-hidden />
      </Button>
    </Tooltip>
  </div>
);
